import { useEffect, useState } from 'react';
import { useParams, Navigate } from 'react-router-dom';
import { supabase } from '@/integrations/supabase/client';
import { Loader2 } from 'lucide-react';
import { BuyButtonFlow } from '@/pages/BuyButtonFlow';
import { SessionTracker } from './SessionTracker';

type Flow = {
  id: string;
  flow_type: string;
};

export const FlowRouter = () => {
  const { flowId } = useParams();
  const [flow, setFlow] = useState<Flow | null>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);

  // Check if the flow is being embedded (e.g., through an iframe)
  const isEmbedded = window.self !== window.top;

  useEffect(() => {
    const fetchFlow = async () => {
      try {
        if (!flowId) {
          throw new Error('No flow ID provided');
        }

        const { data, error } = await supabase
          .from('flows')
          .select('id, flow_type')
          .eq('id', flowId)
          .single();

        if (error) throw error;
        setFlow(data);
      } catch (err) {
        setError(err instanceof Error ? err.message : 'Failed to load flow');
      } finally {
        setLoading(false);
      }
    };

    fetchFlow();
  }, [flowId]);

  if (loading) {
    return (
      <>
        {isEmbedded && flowId && <SessionTracker flowId={flowId} />}
        <div className="min-h-screen flex items-center justify-center">
          <Loader2 className="h-8 w-8 animate-spin" />
        </div>
      </>
    );
  }

  if (error || !flow) {
    return (
      <>
        {isEmbedded && flowId && <SessionTracker flowId={flowId} />}
        <div className="min-h-screen flex items-center justify-center text-red-500">
          Error loading flow. Please try again.
        </div>
      </>
    );
  }

  // Normalize the flow type for comparison
  const normalizedFlowType = flow.flow_type?.trim().toLowerCase() || '';

  // If it's a buy button and it's embedded, show the button UI
  if (normalizedFlowType === 'buy_button' && isEmbedded) {
    return (
      <>
        <SessionTracker flowId={flowId} />
        <BuyButtonFlow />
      </>
    );
  }

  // Get the target route based on flow type
  // Subscription gating is handled by FlowAccessGate on the target routes
  let targetRoute: string;
  switch (normalizedFlowType) {
    case 'payment':
    case 'subscription':
    case 'discount':
    case 'pre-order':
    case 'pre_order':
    case 'preorder':
      targetRoute = `/payment/${flowId}`;
      break;

    case 'pricing_table':
    case 'pricingtable':
    case 'pricing-table':
      targetRoute = `/pricing-table/${flowId}`;
      break;

    case 'buy_button':
      targetRoute = `/buy-button/${flowId}`;
      break;

    case 'referral':
      targetRoute = `/referral/${flowId}`;
      break;

    case 'newsletter':
      targetRoute = `/newsletter/${flowId}`;
      break;

    case 'waitlist':
    case 'signup':
    case 'sign_up':
    case 'sign-up':
      targetRoute = `/signup/${flowId}`;
      break;

    default:
      return (
        <>
          {isEmbedded && flowId && <SessionTracker flowId={flowId} />}
          <Navigate to="/404" replace />
        </>
      );
  }

  // For embedded flows, include session tracking
  if (isEmbedded) {
    return (
      <>
        <SessionTracker flowId={flowId} />
        <Navigate to={targetRoute} replace />
      </>
    );
  }

  // For non-embedded flows, navigate directly
  return <Navigate to={targetRoute} replace />;
};
